import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import { Check as CheckIcon, Edit as EditIcon } from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { getSubscriptionsAPI, updateSubscriptionAPI } from '../../lib/api';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import EditSubscriptionDialog from './EditSubscriptionDialog';

const Subscriptions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    const fetchPlans = async () => {
      try {
        const res = await getSubscriptionsAPI();
        if (res.status && Array.isArray(res.data)) {
          setPlans(res.data);
        } else {
          throw new Error(res.message || 'Invalid response');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedPlan(null);
  };

  const handleSaveSubscription = async (subscriptionId, subscriptionData) => {
    try {
      const result = await updateSubscriptionAPI(subscriptionId, subscriptionData);

      if (result.status && result.data) {
        setPlans(prevPlans =>
          prevPlans.map(plan =>
            plan.id === subscriptionId ? result.data : plan
          )
        );
        setSuccessMessage(`${result.data.name} plan updated successfully!`);
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      throw err;
    }
  };


  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Subscriptions' }
        ]}
      />

      {loading && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Grid container spacing={3}>
          {plans.map((plan) => {
            const featureList = [
              plan.number_of_projects > 0
                ? `${plan.number_of_projects} Project${plan.number_of_projects > 1 ? 's' : ''}`
                : 'Unlimited Projects',
              plan.storage
                ? `${plan.storage}MB Storage`
                : 'Unlimited Storage',
              plan.number_of_members_per_project > 0
                ? `${plan.number_of_members_per_project} Team Members`
                : 'Unlimited Team Members'
            ];

            return (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card
                  sx={{
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: colors.background.primary,
                    position: 'relative',
                    boxShadow: 'none',
                    border: `1px solid ${colors.alpha.black08 || 'rgba(0,0,0,0.08)'}`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: colors.text.primary,
                          fontSize: '1.1rem'
                        }}
                      >
                        {plan.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: colors.primary.main,
                            fontSize: '2rem'
                          }}
                        >
                          ${plan.price_per_month}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.text.secondary }}
                        >
                          /month
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      {featureList.map((feature, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1 }}>
                          <CheckIcon sx={{ fontSize: 18, color: colors.success.main, mt: '2px' }} />
                          <Typography
                            variant="body2"
                            sx={{ color: colors.text.primary, fontSize: '0.85rem', fontWeight: 500 }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="medium"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(plan)}
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          borderRadius: 2,
                          textTransform: 'none',
                          bgcolor: colors.primary.main,
                          color: colors.common.white,
                          '&:hover': {
                            bgcolor: colors.primary.dark
                          }
                        }}
                      >
                        Edit Plan
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <EditSubscriptionDialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        subscription={selectedPlan}
        onSave={handleSaveSubscription}
      />
    </Box>
  );
};

export default Subscriptions;

